import { useState, useMemo } from 'react';
import { cn } from '@/lib/utils/cn';
import { Input } from '@/components/ui';
import { QuestionWrapper } from './question-wrapper';
import type { QuestionComponentProps } from '@/types';

/**
 * Date/DateTime picker question component.
 * Supports date-only and datetime selection with min/max constraints.
 */
export function DatePicker({
  question,
  value,
  onChange,
  disabled,
  errors,
}: QuestionComponentProps<string | null>) {
  const [inputValue, setInputValue] = useState(value || '');

  const includeTime = question.config?.includeTime ?? question.type === 'datetime';
  const minDate = question.config?.minDate;
  const maxDate = question.config?.maxDate;

  // Determine input type
  const inputType = includeTime ? 'datetime-local' : 'date';

  // Format constraints
  const minFormatted = useMemo(() => {
    if (!minDate) return undefined;
    // Handle relative dates like "today", "today-18y"
    if (minDate === 'today') {
      return new Date().toISOString().split('T')[0];
    }
    const relativeMatch = minDate.match(/^today([+-])(\d+)([dmy])$/);
    if (relativeMatch) {
      const [, op, num, unit] = relativeMatch;
      const date = new Date();
      const delta = parseInt(num, 10) * (op === '-' ? -1 : 1);
      switch (unit) {
        case 'd':
          date.setDate(date.getDate() + delta);
          break;
        case 'm':
          date.setMonth(date.getMonth() + delta);
          break;
        case 'y':
          date.setFullYear(date.getFullYear() + delta);
          break;
      }
      return date.toISOString().split('T')[0];
    }
    return minDate;
  }, [minDate]);

  const maxFormatted = useMemo(() => {
    if (!maxDate) return undefined;
    if (maxDate === 'today') {
      return new Date().toISOString().split('T')[0];
    }
    const relativeMatch = maxDate.match(/^today([+-])(\d+)([dmy])$/);
    if (relativeMatch) {
      const [, op, num, unit] = relativeMatch;
      const date = new Date();
      const delta = parseInt(num, 10) * (op === '-' ? -1 : 1);
      switch (unit) {
        case 'd':
          date.setDate(date.getDate() + delta);
          break;
        case 'm':
          date.setMonth(date.getMonth() + delta);
          break;
        case 'y':
          date.setFullYear(date.getFullYear() + delta);
          break;
      }
      return date.toISOString().split('T')[0];
    }
    return maxDate;
  }, [maxDate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue || null);
  };

  const handleClear = () => {
    setInputValue('');
    onChange(null);
  };

  // Format display value for confirmation
  const displayValue = useMemo(() => {
    if (!value) return null;
    try {
      const date = new Date(value);
      if (includeTime) {
        return date.toLocaleString(undefined, {
          dateStyle: 'full',
          timeStyle: 'short',
        });
      }
      return date.toLocaleDateString(undefined, {
        dateStyle: 'full',
      });
    } catch {
      return value;
    }
  }, [value, includeTime]);

  return (
    <QuestionWrapper question={question} errors={errors}>
      <div className="space-y-4">
        {/* Date/DateTime input */}
        <div className="relative">
          <Input
            type={inputType}
            value={inputValue}
            onChange={handleChange}
            min={minFormatted}
            max={maxFormatted}
            disabled={disabled}
            className={cn(
              'w-full',
              disabled && 'bg-gray-50 cursor-not-allowed'
            )}
          />

          {/* Clear button */}
          {inputValue && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-10 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label="Clear date"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Display formatted date */}
        {displayValue && (
          <div className="flex items-center gap-2 rounded-lg bg-gray-50 p-3 text-gray-700">
            <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span className="text-sm">{displayValue}</span>
          </div>
        )}

        {/* Date constraints hint */}
        {(minFormatted || maxFormatted) && (
          <p className="text-xs text-gray-500">
            {minFormatted && maxFormatted
              ? `Please select a date between ${new Date(minFormatted).toLocaleDateString()} and ${new Date(maxFormatted).toLocaleDateString()}`
              : minFormatted
              ? `Please select a date on or after ${new Date(minFormatted).toLocaleDateString()}`
              : `Please select a date on or before ${new Date(maxFormatted!).toLocaleDateString()}`}
          </p>
        )}
      </div>
    </QuestionWrapper>
  );
}
